/**
 * The product stage: renders a container with printed artwork and, when
 * editable, the placement handles. Used by the configurator (editable) and
 * by the landing hero (auto-rotating demo).
 *
 * Layers (all share one viewBox in mm):
 *   back SVG (body, contents) → ink canvas → gloss SVG → guides/handles SVG
 */
import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as RKeyboardEvent,
  type PointerEvent as RPointerEvent,
  type Ref,
} from 'react';
import type { ArtworkPlacement, InkSetup, MaterialId } from '@shared/rfq';
import {
  ELLIPSE_RATIO,
  cylinderProjection,
  deltaU,
  flatProjection,
  hitsArtwork,
  localToSurface,
  placementCorners,
  round1,
  wrapU,
  type Point,
  type Projection,
  type SurfacePoint,
} from './geometry';
import { ProductBack, ProductGloss } from './ProductSvg';
import { circumference, type ContainerModel } from './model';
import { beginStage, drawCylinder, drawFlat, renderSurface } from './render';

export type StageView = 'front' | 'flat';

export interface StageApi {
  exportMockup(pxWidth?: number): Promise<Blob | null>;
}

export interface StageLabels {
  printBand: string;
  seam: string;
  stageLabel: string;
  noPrint?: string;
}

interface Props {
  container: ContainerModel;
  material: MaterialId;
  ink: InkSetup;
  image: CanvasImageSource | null;
  placement: ArtworkPlacement | null;
  view: StageView;
  viewAngleDeg: number;
  onViewAngleChange?: (deg: number) => void;
  editable?: boolean;
  onPlacementChange?: (p: ArtworkPlacement) => void;
  showGuides?: boolean;
  /** Degrees per second; respects prefers-reduced-motion. */
  autoRotate?: number;
  labels: StageLabels;
  apiRef?: Ref<StageApi>;
  className?: string;
}

type Drag =
  | { mode: 'move'; s0: SurfacePoint; p0: ArtworkPlacement }
  | { mode: 'resize'; d0: number; p0: ArtworkPlacement }
  | { mode: 'rotate'; a0: number; p0: ArtworkPlacement }
  | { mode: 'spin'; x0: number; angle0: number };

const HANDLE_PX = 9;

export function Stage(props: Props) {
  const {
    container: c,
    material,
    ink,
    image,
    placement,
    view,
    viewAngleDeg,
    onViewAngleChange,
    editable = false,
    onPlacementChange,
    showGuides = true,
    autoRotate = 0,
    labels,
    apiRef,
    className,
  } = props;

  const uid = useId().replace(/:/g, '');
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<SVGSVGElement>(null);
  const backRef = useRef<SVGSVGElement>(null);
  const glossRef = useRef<SVGSVGElement>(null);
  const surfaceRef = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [drag, setDrag] = useState<Drag | null>(null);
  const [spinAngle, setSpinAngle] = useState(viewAngleDeg);

  // Auto-rotation drives an internal angle; otherwise follow the prop.
  const reduceMotion = useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches, []);
  useEffect(() => {
    if (!autoRotate || reduceMotion) {
      setSpinAngle(viewAngleDeg);
      return;
    }
    let raf = 0;
    let last = performance.now();
    let a = viewAngleDeg;
    const tick = (t: number) => {
      a = (a + ((t - last) / 1000) * autoRotate) % 360;
      last = t;
      setSpinAngle(a);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoRotate, reduceMotion, viewAngleDeg]);

  const angle = autoRotate && !reduceMotion ? spinAngle : viewAngleDeg;
  const C = circumference(c);
  const H = c.print.heightMm;

  const proj: Projection = useMemo(
    () => (view === 'flat' ? flatProjection(c, wrapU(((angle * Math.PI) / 180) * (c.diameterMm / 2), C)) : cylinderProjection(c, angle)),
    [view, c, angle, C],
  );
  const vb = proj.viewBox;

  // Track CSS size for crisp canvas output.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setSize({ w: entry.contentRect.width, h: entry.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Surface texture only changes with artwork, placement or container.
  const surface = useMemo(() => {
    surfaceRef.current ??= document.createElement('canvas');
    return renderSurface(surfaceRef.current, image, placement, C, H);
  }, [image, placement, C, H]);

  const draw = useCallback(
    (canvas: HTMLCanvasElement, cssW: number, cssH: number, dpr: number) => {
      const ctx = beginStage({ canvas, viewBox: vb, cssWidth: cssW, cssHeight: cssH, dpr });
      if (!image || !placement) return;
      if (proj.kind === 'cylinder') drawCylinder(ctx, surface, c, angle, { material, ink });
      else drawFlat(ctx, surface, C, H, { material, ink });
    },
    [vb, image, placement, proj.kind, surface, c, angle, material, ink, C, H],
  );

  useEffect(() => {
    if (!canvasRef.current || !size.w) return;
    draw(canvasRef.current, size.w, size.h, Math.min(2, window.devicePixelRatio || 1));
  }, [draw, size]);

  useImperativeHandle(
    apiRef,
    () => ({
      async exportMockup(pxWidth = 1400) {
        const pxH = Math.round((pxWidth * vb.h) / vb.w);
        const out = document.createElement('canvas');
        out.width = pxWidth;
        out.height = pxH;
        const octx = out.getContext('2d')!;
        const inkCanvas = document.createElement('canvas');
        draw(inkCanvas, pxWidth, pxH, 1);
        const svgToImage = async (svg: SVGSVGElement | null) => {
          if (!svg) return null;
          const clone = svg.cloneNode(true) as SVGSVGElement;
          clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          clone.setAttribute('width', String(pxWidth));
          clone.setAttribute('height', String(pxH));
          const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' }));
          const img = new Image();
          img.src = url;
          try {
            await img.decode();
            return img;
          } catch {
            return null;
          } finally {
            URL.revokeObjectURL(url);
          }
        };
        const back = await svgToImage(backRef.current);
        const gloss = await svgToImage(glossRef.current);
        if (back) octx.drawImage(back, 0, 0, pxWidth, pxH);
        octx.drawImage(inkCanvas, 0, 0, pxWidth, pxH);
        if (gloss) octx.drawImage(gloss, 0, 0, pxWidth, pxH);
        return new Promise<Blob | null>((res) => out.toBlob(res, 'image/png'));
      },
    }),
    [draw, vb],
  );

  // ------------------------------------------------------------ interaction
  const mmPerPx = size.w ? vb.w / size.w : 0.3;
  const handleR = HANDLE_PX * mmPerPx;

  const toStage = (e: { clientX: number; clientY: number }): Point | null => {
    const svg = overlayRef.current;
    const m = svg?.getScreenCTM();
    if (!svg || !m) return null;
    const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return { x: pt.x, y: pt.y };
  };

  const handles = useMemo(() => {
    if (!placement) return null;
    const corners = placementCorners(placement).map((s) => ({ s, p: proj.toStage(s) }));
    const rotS = localToSurface(placement, 0, -placement.heightMm / 2 - Math.max(6, 16 * mmPerPx));
    const topS = localToSurface(placement, 0, -placement.heightMm / 2);
    return { corners, rot: proj.toStage(rotS), top: proj.toStage(topS) };
  }, [placement, proj, mmPerPx]);

  const commit = (p: ArtworkPlacement) =>
    onPlacementChange?.({
      uMm: round1(wrapU(p.uMm, C)),
      vMm: round1(p.vMm),
      widthMm: round1(p.widthMm),
      heightMm: round1(p.heightMm),
      rotationDeg: Math.round(p.rotationDeg * 10) / 10,
    });

  const onPointerDown = (e: RPointerEvent<SVGSVGElement>) => {
    const pt = toStage(e);
    if (!pt) return;
    const dist = (a: Point) => Math.hypot(a.x - pt.x, a.y - pt.y);

    if (editable && placement && handles) {
      if (handles.rot.visible && dist(handles.rot) <= handleR * 1.6) {
        const s = proj.toSurface(pt);
        if (s) {
          const a0 = Math.atan2(s.v - placement.vMm, deltaU(placement.uMm, s.u, C));
          start({ mode: 'rotate', a0, p0: placement }, e);
          return;
        }
      }
      for (const k of handles.corners) {
        if (k.p.visible && dist(k.p) <= handleR * 1.6) {
          const d0 = Math.hypot(deltaU(placement.uMm, k.s.u, C), k.s.v - placement.vMm);
          start({ mode: 'resize', d0, p0: placement }, e);
          return;
        }
      }
      const s = proj.toSurface(pt);
      if (s && hitsArtwork(placement, s, C, 1)) {
        start({ mode: 'move', s0: s, p0: placement }, e);
        return;
      }
    }
    if (proj.kind === 'cylinder' && onViewAngleChange) start({ mode: 'spin', x0: pt.x, angle0: angle }, e);
  };

  const start = (d: Drag, e: RPointerEvent<SVGSVGElement>) => {
    e.preventDefault();
    overlayRef.current?.setPointerCapture(e.pointerId);
    overlayRef.current?.focus({ preventScroll: true });
    setDrag(d);
  };

  const onPointerMove = (e: RPointerEvent<SVGSVGElement>) => {
    if (!drag) return;
    const pt = toStage(e);
    if (!pt) return;
    if (drag.mode === 'spin') {
      const r = c.diameterMm / 2;
      const deg = ((pt.x - drag.x0) / (Math.PI * r)) * -180;
      onViewAngleChange?.(((drag.angle0 + deg) % 360 + 360) % 360);
      return;
    }
    const s = proj.toSurface(pt);
    if (!s) return;
    const p0 = drag.p0;
    if (drag.mode === 'move') {
      commit({ ...p0, uMm: p0.uMm + deltaU(drag.s0.u, s.u, C), vMm: p0.vMm + (s.v - drag.s0.v) });
    } else if (drag.mode === 'resize') {
      const d = Math.hypot(deltaU(p0.uMm, s.u, C), s.v - p0.vMm);
      const k = Math.max(4 / p0.widthMm, Math.min((C * 1.5) / p0.widthMm, d / Math.max(0.5, drag.d0)));
      commit({ ...p0, widthMm: p0.widthMm * k, heightMm: p0.heightMm * k });
    } else if (drag.mode === 'rotate') {
      const a = Math.atan2(s.v - p0.vMm, deltaU(p0.uMm, s.u, C));
      let deg = p0.rotationDeg + ((a - drag.a0) * 180) / Math.PI;
      deg = ((deg % 360) + 540) % 360 - 180;
      const step = e.shiftKey ? 15 : 90;
      const snap = Math.round(deg / step) * step;
      if (e.shiftKey || Math.abs(deg - snap) < 3) deg = snap;
      commit({ ...p0, rotationDeg: deg });
    }
  };

  const onPointerUp = (e: RPointerEvent<SVGSVGElement>) => {
    if (overlayRef.current?.hasPointerCapture(e.pointerId)) overlayRef.current.releasePointerCapture(e.pointerId);
    setDrag(null);
  };

  const onKeyDown = (e: RKeyboardEvent<SVGSVGElement>) => {
    if (!editable || !placement) return;
    const step = e.shiftKey ? 5 : 1;
    const map: Record<string, Partial<ArtworkPlacement>> = {
      ArrowLeft: { uMm: placement.uMm - step },
      ArrowRight: { uMm: placement.uMm + step },
      ArrowUp: { vMm: placement.vMm - step },
      ArrowDown: { vMm: placement.vMm + step },
    };
    let next: ArtworkPlacement | null = map[e.key] ? { ...placement, ...map[e.key] } : null;
    if (e.key === '+' || e.key === '=') next = { ...placement, widthMm: placement.widthMm * 1.05, heightMm: placement.heightMm * 1.05 };
    if (e.key === '-' || e.key === '_') next = { ...placement, widthMm: placement.widthMm / 1.05, heightMm: placement.heightMm / 1.05 };
    if (e.key === '[' || e.key === ']') next = { ...placement, rotationDeg: placement.rotationDeg + (e.key === ']' ? 1 : -1) * (e.shiftKey ? 15 : 1) };
    if (next) {
      e.preventDefault();
      commit(next);
    }
  };

  // ------------------------------------------------------------ overlay geometry
  const outline = useMemo(() => {
    if (!placement) return '';
    const corners = placementCorners(placement);
    let d = '';
    let pen = false;
    for (let i = 0; i < 4; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % 4];
      for (let t = 0; t <= 24; t++) {
        const s = { u: a.u + ((b.u - a.u) * t) / 24, v: a.v + ((b.v - a.v) * t) / 24 };
        const p = proj.toStage(s);
        if (!p.visible) {
          pen = false;
          continue;
        }
        d += `${pen ? 'L' : 'M'}${p.x.toFixed(2)} ${p.y.toFixed(2)} `;
        pen = true;
      }
    }
    return d;
  }, [placement, proj]);

  const r = c.diameterMm / 2;
  const e = ELLIPSE_RATIO;
  const bandTop = c.print.topMm;
  const stroke = Math.max(0.25, 1.2 * mmPerPx);
  const fontSize = Math.max(2.6, 11 * mmPerPx);

  const seam = proj.kind === 'cylinder' ? proj.toStage({ u: 0, v: 0 }) : null;

  return (
    <div
      ref={wrapRef}
      className={`stage stage--${view}${editable ? ' stage--editable' : ''}${drag ? ' stage--dragging' : ''} ${className ?? ''}`}
      style={{ aspectRatio: `${vb.w} / ${vb.h}`, ['--ar' as string]: vb.w / vb.h }}
    >
      <svg ref={backRef} className="stage__layer" viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} aria-hidden="true">
        {view === 'front' ? (
          <ProductBack c={c} material={material} uid={uid} viewAngleDeg={angle} />
        ) : (
          <g>
            <rect x="0" y="0" width={C} height={H} className="stage__strip" />
            {Array.from({ length: Math.floor(C / 10) + 1 }, (_, i) => (
              <line key={`g${i}`} x1={i * 10} x2={i * 10} y1="0" y2={H} className="stage__grid" strokeWidth={stroke * 0.6} />
            ))}
            {Array.from({ length: Math.floor(H / 10) + 1 }, (_, i) => (
              <line key={`h${i}`} x1="0" x2={C} y1={i * 10} y2={i * 10} className="stage__grid" strokeWidth={stroke * 0.6} />
            ))}
          </g>
        )}
      </svg>
      <canvas ref={canvasRef} className="stage__layer stage__canvas" aria-hidden="true" />
      <svg ref={glossRef} className="stage__layer" viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} aria-hidden="true">
        {view === 'front' && <ProductGloss c={c} material={material} uid={uid} viewAngleDeg={angle} />}
      </svg>
      <svg
        ref={overlayRef}
        className="stage__layer stage__overlay"
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        tabIndex={editable || onViewAngleChange ? 0 : -1}
        role={editable ? 'application' : 'img'}
        aria-label={labels.stageLabel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        {showGuides && view === 'front' && (
          <g className="guides" strokeWidth={stroke}>
            <path d={`M ${-r} ${bandTop} A ${r} ${e * r} 0 0 0 ${r} ${bandTop}`} className="guides__band" />
            <path d={`M ${-r} ${bandTop + H} A ${r} ${e * r} 0 0 0 ${r} ${bandTop + H}`} className="guides__band" />
            {/* dimension line */}
            <g className="guides__dim">
              <line x1={r + 6} x2={r + 6} y1={bandTop + e * r * 0.2} y2={bandTop + H + e * r * 0.2} />
              <line x1={r + 3.5} x2={r + 8.5} y1={bandTop + e * r * 0.2} y2={bandTop + e * r * 0.2} />
              <line x1={r + 3.5} x2={r + 8.5} y1={bandTop + H + e * r * 0.2} y2={bandTop + H + e * r * 0.2} />
              <text x={r + 8} y={bandTop + H / 2} fontSize={fontSize} transform={`rotate(90 ${r + 8} ${bandTop + H / 2})`} textAnchor="middle" dy={-fontSize * 0.4}>
                {round1(H)} мм
              </text>
            </g>
            <text
              x={-r - 6}
              y={bandTop + H / 2}
              fontSize={fontSize}
              className="guides__label"
              textAnchor="middle"
              transform={`rotate(-90 ${-r - 6} ${bandTop + H / 2})`}
            >
              {labels.printBand}
            </text>
            {seam?.visible && (
              <g className="guides__seam">
                <line x1={seam.x} x2={seam.x} y1={seam.y} y2={seam.y + H} />
                <text x={seam.x + 1.5} y={seam.y + H - 2} fontSize={fontSize * 0.9}>
                  {labels.seam}
                </text>
              </g>
            )}
          </g>
        )}
        {showGuides && view === 'flat' && (
          <g className="guides" strokeWidth={stroke}>
            <rect x="0" y="0" width={C} height={H} className="guides__band" fill="none" />
            {[
              [0, 0],
              [C, 0],
              [0, H],
              [C, H],
            ].map(([x, y], i) => (
              <g key={i} className="guides__crop">
                <line x1={x + (x ? 3 : -3)} x2={x + (x ? 10 : -10)} y1={y} y2={y} />
                <line x1={x} x2={x} y1={y + (y ? 3 : -3)} y2={y + (y ? 10 : -10)} />
              </g>
            ))}
            <line x1={proj.frontU} x2={proj.frontU} y1={-4} y2={H + 4} className="guides__front" />
            {c.noPrint?.map((z, i) => (
              <g key={`np${i}`} className="guides__noprint">
                <rect x={z.uMm - z.widthMm / 2} y={0} width={z.widthMm} height={H} />
                <text x={z.uMm} y={H / 2} fontSize={fontSize} textAnchor="middle" transform={`rotate(-90 ${z.uMm} ${H / 2})`}>
                  {labels.noPrint ?? ''}
                </text>
              </g>
            ))}
            <text x={2} y={H + fontSize * 1.6} fontSize={fontSize} className="guides__label">
              {labels.seam} · 0
            </text>
            <text x={C - 2} y={H + fontSize * 1.6} fontSize={fontSize} className="guides__label" textAnchor="end">
              {round1(C)} мм · {labels.seam}
            </text>
            <text x={C / 2} y={-fontSize * 0.8} fontSize={fontSize} className="guides__label" textAnchor="middle">
              {labels.printBand} {round1(C)} × {round1(H)} мм
            </text>
          </g>
        )}
        {editable && placement && handles && (
          <g className="sel" strokeWidth={stroke}>
            <path d={outline} className="sel__outline" />
            {handles.top.visible && handles.rot.visible && (
              <line x1={handles.top.x} y1={handles.top.y} x2={handles.rot.x} y2={handles.rot.y} className="sel__stem" />
            )}
            {handles.rot.visible && <circle cx={handles.rot.x} cy={handles.rot.y} r={handleR} className="sel__rot" />}
            {handles.corners.map((k, i) =>
              k.p.visible ? (
                <rect key={i} x={k.p.x - handleR * 0.8} y={k.p.y - handleR * 0.8} width={handleR * 1.6} height={handleR * 1.6} className="sel__handle" />
              ) : null,
            )}
          </g>
        )}
      </svg>
    </div>
  );
}
